import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import PromoteDialog from "@/components/PromoteDialog";
import Pager from "@/components/Pager";
import ConsultantPicker from "@/components/ConsultantPicker";
import FileUploader, { FileGrid } from "@/components/FileUploader";
import LeadBulkBar from "@/components/LeadBulkBar";
import RejectLeadDialog from "@/components/RejectLeadDialog";
import Timeline from "@/components/Timeline";
import { Checkbox } from "@/components/ui/checkbox";
import { SOURCE_LABEL, PRIORITIES, PRIORITY_COLORS, LEAD_STATUSES, LEAD_REJECTION_LABEL, fmtDay } from "@/lib/meta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, MessageSquare, Upload, Loader2, ArrowUpRight, Copy, Search, Sparkles, AlertTriangle, Pencil, XCircle, RotateCcw, MapPin } from "lucide-react";
import { toast } from "sonner";

function PriorityBadge({ p }) {
  if (!p) return null;
  return <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold capitalize ${PRIORITY_COLORS[p] || PRIORITY_COLORS.medium}`} data-testid={`priority-${p}`}>{p}</span>;
}

export default function Leads() {
  const [tab, setTab] = useState("all");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const LIMIT = 50;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [promoteLead, setPromoteLead] = useState(null);
  const [mapLead, setMapLead] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [cityFilter, setCityFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [allMatching, setAllMatching] = useState(false);
  const [rejectLead, setRejectLead] = useState(null);

  const filterParams = useCallback(() => {
    const p = tab === "review" ? { review: true } : {};
    if (search) p.search = search;
    if (statusFilter) p.status = statusFilter;
    if (priorityFilter) p.priority = priorityFilter;
    if (cityFilter) p.city = cityFilter;
    return p;
  }, [tab, search, statusFilter, priorityFilter, cityFilter]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { ...filterParams(), limit: LIMIT, skip };
    api.get("/leads", { params }).then(({ data }) => { setRows(data.items); setTotal(data.total); }).finally(() => setLoading(false));
  }, [filterParams, skip]);
  useEffect(() => { setSkip(0); }, [filterParams]);
  useEffect(() => { setSelected([]); setAllMatching(false); }, [filterParams]);
  useEffect(() => { load(); }, [load]);

  const pageIds = rows.map((r) => r.id);
  const pageAll = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const togglePage = () => { setAllMatching(false); setSelected(pageAll ? selected.filter((id) => !pageIds.includes(id)) : Array.from(new Set([...selected, ...pageIds]))); };
  const toggleOne = (id) => { setAllMatching(false); setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]); };
  const clearSel = () => { setSelected([]); setAllMatching(false); };
  const selCount = allMatching ? total : selected.length;
  const selection = allMatching ? { filter: filterParams() } : { ids: selected };

  const doReject = async (code, note) => {
    try { await api.post(`/leads/${rejectLead.id}/reject`, { code, note }); toast.success("Lead rejected — moved to Properties › Rejected Archive"); setRejectLead(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const restore = async (id) => { try { await api.post(`/leads/${id}/restore`); load(); toast.success("Lead restored"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const markDup = async (id) => { try { await api.post(`/leads/${id}/mark-duplicate`); load(); toast.success("Marked as duplicate"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const resolveReview = async (id) => { try { await api.post(`/leads/${id}/resolve-review`); load(); toast.success("Cleared from review"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  return (
    <div>
      <PageHeader title="Leads" subtitle="Intake from every channel. Review, then promote to a deal.">
        <Button variant="outline" size="sm" onClick={() => setWaOpen(true)} data-testid="wa-paste-btn"><MessageSquare className="h-4 w-4" /> Paste WhatsApp</Button>
        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-btn"><Upload className="h-4 w-4" /> Import CSV</Button>
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="add-lead-btn"><Plus className="h-4 w-4" /> Add Lead</Button>
      </PageHeader>

      <div className="px-8 py-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="leads-tabs">
              <TabsTrigger value="all" data-testid="tab-all">All Leads</TabsTrigger>
              <TabsTrigger value="review" data-testid="tab-review">Needs Review</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-36" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All statuses</SelectItem>
                {LEAD_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={priorityFilter || "all"} onValueChange={(v) => setPriorityFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-32" data-testid="priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All priority</SelectItem>
                {PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative w-44">
              <MapPin className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input placeholder="City / location" value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} className="pl-8" data-testid="leads-city-filter" />
            </div>
            <div className="relative w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input placeholder="Search name, phone, city…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" data-testid="leads-search" />
            </div>
          </div>
        </div>

        {selCount > 0 && (
          <>
            <LeadBulkBar selection={selection} count={selCount} onClear={clearSel} onDone={() => { clearSel(); load(); }} />
            {pageAll && !allMatching && total > rows.length && (
              <div className="text-xs text-gray-600 mb-3 -mt-1" data-testid="select-all-matching-banner">
                All {rows.length} on this page are selected. <button className="text-[#0047FF] font-semibold hover:underline" onClick={() => setAllMatching(true)} data-testid="select-all-matching">Select all {total} matching leads</button>
              </div>
            )}
            {allMatching && <div className="text-xs text-gray-600 mb-3 -mt-1" data-testid="all-matching-note">All {total} leads matching the current filters are selected.</div>}
          </>
        )}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm" data-testid="leads-empty">No leads {tab === "review" ? "need review" : "yet"}.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-10"><Checkbox checked={pageAll || allMatching} onCheckedChange={togglePage} data-testid="select-page-checkbox" /></TableHead>
                  <TableHead>Name</TableHead><TableHead>Phone</TableHead><TableHead>City</TableHead>
                  <TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead>Added</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((l) => (
                  <TableRow key={l.id} className={`hover:bg-gray-50 ${l.status === "rejected" ? "opacity-60" : ""}`} data-testid={`lead-row-${l.id}`}>
                    <TableCell><Checkbox checked={allMatching || selected.includes(l.id)} onCheckedChange={() => toggleOne(l.id)} data-testid={`select-lead-${l.id}`} /></TableCell>
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">{l.name || <span className="text-gray-400">—</span>}<PriorityBadge p={l.priority} /></span>
                      {l.needs_review && <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><AlertTriangle className="h-3 w-3" />review</span>}
                      {l.consultant_name && <div className="text-[11px] text-gray-400 mt-0.5">via {l.consultant_name}</div>}
                      {l.unmapped && Object.keys(l.unmapped).length > 0 && (
                        <div className="text-[11px] text-gray-400 mt-0.5">Unmapped: {Object.keys(l.unmapped).join(", ")}</div>
                      )}
                      {l.dup_candidate_id && <div className="text-[11px] text-rose-500 mt-0.5">Possible duplicate</div>}
                    </TableCell>
                    <TableCell>{l.phone || "—"}</TableCell>
                    <TableCell>{l.city || "—"}</TableCell>
                    <TableCell><span className="text-xs bg-gray-100 rounded px-1.5 py-0.5">{SOURCE_LABEL[l.source] || l.source}</span></TableCell>
                    <TableCell>
                      <span className={`text-xs capitalize ${l.status === "rejected" ? "text-rose-600 font-semibold" : ""}`} data-testid={`lead-status-${l.id}`}>{(l.status || "").replace(/_/g, " ")}</span>
                      {l.status === "rejected" && l.rejection_reason && (
                        <div className="text-[11px] text-gray-500" data-testid={`lead-reject-reason-${l.id}`}>{LEAD_REJECTION_LABEL[l.rejection_reason.code] || l.rejection_reason.code}{l.rejection_reason.note ? ` · ${l.rejection_reason.note}` : ""}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">{fmtDay(l.created_at)}</TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      {!["promoted", "duplicate", "rejected", "merged"].includes(l.status) && (
                        <Button size="sm" variant="outline" onClick={() => setPromoteLead(l)} data-testid={`promote-${l.id}`}><ArrowUpRight className="h-3.5 w-3.5" /> Promote</Button>
                      )}
                      {!["promoted", "rejected", "merged"].includes(l.status) && (
                        <Button size="sm" variant="ghost" title="Reject" onClick={() => setRejectLead(l)} data-testid={`reject-${l.id}`}><XCircle className="h-3.5 w-3.5 text-rose-500" /></Button>
                      )}
                      {l.status === "rejected" && (
                        <Button size="sm" variant="outline" onClick={() => restore(l.id)} data-testid={`restore-${l.id}`}><RotateCcw className="h-3.5 w-3.5" /> Restore</Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => setEditLead(l)} data-testid={`edit-${l.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      {l.unmapped && Object.keys(l.unmapped).length > 0 && (
                        <Button size="sm" variant="outline" onClick={() => setMapLead(l)} data-testid={`map-${l.id}`}>Map</Button>
                      )}
                      {l.needs_review && <Button size="sm" variant="ghost" onClick={() => resolveReview(l.id)} data-testid={`resolve-${l.id}`}>Resolve</Button>}
                      {!["duplicate", "promoted", "rejected"].includes(l.status) && (
                        <Button size="sm" variant="ghost" onClick={() => markDup(l.id)} data-testid={`dup-${l.id}`}><Copy className="h-3.5 w-3.5" /></Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="text-xs text-gray-400">{total} lead(s)</div>
          <Pager skip={skip} limit={LIMIT} total={total} onPrev={() => setSkip(Math.max(0, skip - LIMIT))} onNext={() => setSkip(skip + LIMIT)} />
        </div>
      </div>

      <LeadDialog open={addOpen} onOpenChange={setAddOpen} onSaved={load} />
      <LeadDialog lead={editLead} open={!!editLead} onOpenChange={(v) => !v && setEditLead(null)} onSaved={() => { setEditLead(null); load(); }} />
      <WhatsAppDialog open={waOpen} onOpenChange={setWaOpen} onSaved={load} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
      <PromoteDialog lead={promoteLead} open={!!promoteLead} onOpenChange={(v) => !v && setPromoteLead(null)} onPromoted={() => { setPromoteLead(null); load(); }} />
      <RejectLeadDialog open={!!rejectLead} onOpenChange={(v) => !v && setRejectLead(null)} onConfirm={doReject} />
      <MapColumnsDialog lead={mapLead} open={!!mapLead} onOpenChange={(v) => !v && setMapLead(null)} onSaved={() => { setMapLead(null); load(); }} />
    </div>
  );
}

const EMPTY = { name: "", phone: "", email: "", city: "", location: "", num_rooms: "", rental_expectation: "", notes: "", priority: "medium", consultant_id: "", consultant_name: "", photos: [] };

function LeadFields({ form, setForm, showStatus }) {
  const addPhoto = async (path) => setForm((f) => ({ ...f, photos: [...(f.photos || []), path] }));
  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="lead-name" /></div>
      <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" data-testid="lead-phone" /></div>
      <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" data-testid="lead-email" /></div>
      <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1" data-testid="lead-city" /></div>
      <div><Label>Rooms</Label><Input value={form.num_rooms} onChange={(e) => setForm({ ...form, num_rooms: e.target.value })} className="mt-1" data-testid="lead-rooms" /></div>
      <div><Label>Rental expectation</Label><Input value={form.rental_expectation} onChange={(e) => setForm({ ...form, rental_expectation: e.target.value })} className="mt-1" data-testid="lead-rental" /></div>
      <div>
        <Label>Priority</Label>
        <Select value={form.priority || "medium"} onValueChange={(v) => setForm({ ...form, priority: v })}>
          <SelectTrigger className="mt-1" data-testid="lead-priority"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{PRIORITIES.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {showStatus && (
        <div>
          <Label>Status</Label>
          <Select value={form.status || "new"} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="mt-1" data-testid="lead-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="new">New</SelectItem><SelectItem value="follow_up">Follow Up</SelectItem><SelectItem value="duplicate">Duplicate</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="col-span-2"><Label>Consultant (optional)</Label><div className="mt-1"><ConsultantPicker value={form.consultant_id} onChange={(id, name) => setForm({ ...form, consultant_id: id, consultant_name: name })} /></div></div>
      <div className="col-span-2"><Label>Location / Address</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" data-testid="lead-location" /></div>
      <div className="col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" data-testid="lead-notes" /></div>
      <div className="col-span-2">
        <div className="flex items-center justify-between mb-1"><Label>Photos</Label><FileUploader label="Add photo" onUploaded={addPhoto} testid="lead-photo" /></div>
        <FileGrid files={form.photos} />
      </div>
    </div>
  );
}

function LeadDialog({ lead, open, onOpenChange, onSaved }) {
  const isEdit = !!lead;
  const [form, setForm] = useState(EMPTY);
  const [dups, setDups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    if (open) {
      setForm(isEdit ? { ...EMPTY, ...lead, photos: lead.photos || [], status: lead.status } : EMPTY);
      setDups([]); setEvents([]);
      if (isEdit) api.get(`/leads/${lead.id}/events`).then(({ data }) => setEvents(data)).catch(() => {});
    }
  }, [open, lead]);

  const checkDup = async () => {
    if (!form.phone && !form.email) return;
    try { const { data } = await api.get("/leads/check-duplicate", { params: { phone: form.phone, email: form.email, exclude: lead?.id } }); setDups(data); } catch {}
  };

  const save = async () => {
    setBusy(true);
    try {
      if (isEdit) await api.put(`/leads/${lead.id}`, form);
      else await api.post("/leads", form);
      toast.success(isEdit ? "Lead updated" : "Lead added");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Edit lead" : "Add lead manually"}</DialogTitle></DialogHeader>
        <div onBlur={isEdit ? undefined : checkDup}><LeadFields form={form} setForm={setForm} showStatus={isEdit && !["rejected", "promoted"].includes(lead?.status)} /></div>
        {isEdit && events.length > 0 && (
          <div className="border-t border-gray-100 pt-3" data-testid="lead-history">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">History</div>
            <Timeline events={events} />
          </div>
        )}
        {dups.length > 0 && (
          <div className="text-xs bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2" data-testid="dup-warning">
            {dups.length} possible duplicate(s): {dups.map((d) => d.name || d.phone).join(", ")}. Consider editing the existing lead instead.
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="save-lead-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {isEdit ? "Save" : "Add lead"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WhatsAppDialog({ open, onOpenChange, onSaved }) {
  const [text, setText] = useState("");
  const [form, setForm] = useState(null);
  const [ai, setAi] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setText(""); setForm(null); } }, [open]);

  const parse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads/parse-whatsapp", { text });
      const p = data.parsed || {};
      setForm({ ...EMPTY, name: p.name || "", phone: p.phone || "", email: p.email || "", city: p.city || "", location: p.location || "", num_rooms: p.num_rooms || "", rental_expectation: p.rental_expectation || "", notes: p.notes || "" });
      setAi(data.ai);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try { await api.post("/leads", { ...form, source: "whatsapp" }); toast.success(form.consultant_id ? "Consultant lead added" : "Lead added"); onOpenChange(false); onSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="wa-dialog">
        <DialogHeader>
          <DialogTitle>Paste a WhatsApp message</DialogTitle>
          <DialogDescription>AI cleans and extracts the fields. Review, pick the consultant if any, then save — nothing is saved automatically.</DialogDescription>
        </DialogHeader>
        {!form ? (
          <>
            <Textarea rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste the forwarded message here…" data-testid="wa-text" />
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={parse} disabled={busy || !text.trim()} data-testid="wa-parse-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Extract fields</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="text-xs text-gray-500 mb-1">{ai ? "Extracted by AI" : "Extracted by pattern match"} — please review. Select a consultant to make it a consultant lead.</div>
            <LeadFields form={form} setForm={setForm} />
            <DialogFooter>
              <Button variant="ghost" onClick={() => setForm(null)}>Back</Button>
              <Button onClick={save} disabled={busy} data-testid="wa-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save lead</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const SHEET_TYPES = [
  { key: "main", label: "Main sheet" }, { key: "whatsapp", label: "WhatsApp form" },
  { key: "facebook", label: "Facebook / Meta" }, { key: "ai_screening", label: "AI screening" },
  { key: "consultant", label: "Consultants" }, { key: "owner", label: "Property owners" },
];

function ImportDialog({ open, onOpenChange, onDone }) {
  const [type, setType] = useState("main");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  useEffect(() => { if (open) { setFile(null); setResult(null); } }, [open]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/import/${type}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data); onDone();
      toast.success(`Imported ${data.inserted} row(s)`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="import-dialog">
        <DialogHeader><DialogTitle>Import CSV</DialogTitle><DialogDescription>Unmapped columns are kept and flagged for review — never dropped.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Sheet format</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              {SHEET_TYPES.map((s) => (
                <button key={s.key} onClick={() => setType(s.key)} data-testid={`import-type-${s.key}`}
                  className={`text-xs px-2 py-1.5 rounded border transition-colors ${type === s.key ? "border-[#0047FF] bg-blue-50 text-[#0047FF]" : "border-gray-200 text-gray-600"}`}>{s.label}</button>
              ))}
            </div>
          </div>
          <Input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file" />
          {result && (
            <div className="text-xs bg-gray-50 border border-gray-200 rounded p-2" data-testid="import-result">
              Inserted {result.inserted}{result.flagged_review ? `, ${result.flagged_review} flagged for review` : ""}{result.flagged_duplicates ? `, ${result.flagged_duplicates} possible duplicates` : ""}{result.skipped ? `, ${result.skipped} duplicates skipped` : ""}.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={upload} disabled={busy || !file} data-testid="import-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Import</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MAP_TARGETS = [
  { key: "name", label: "Name" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" },
  { key: "city", label: "City" }, { key: "location", label: "Location / Address" },
  { key: "num_rooms", label: "Rooms" }, { key: "rental_expectation", label: "Rental expectation" }, { key: "notes", label: "Notes" },
];

function MapColumnsDialog({ lead, open, onOpenChange, onSaved }) {
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setMapping({}); }, [open]);
  const entries = lead?.unmapped ? Object.entries(lead.unmapped) : [];

  const save = async () => {
    setBusy(true);
    try {
      const mappings = {}; const discard = [];
      entries.forEach(([k]) => { const t = mapping[k]; if (t && t !== "ignore") mappings[k] = t; else discard.push(k); });
      await api.post(`/leads/${lead.id}/map-columns`, { mappings, discard });
      toast.success("Columns mapped — lead cleared from review");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg" data-testid="map-columns-dialog">
        <DialogHeader>
          <DialogTitle>Map unrecognised columns</DialogTitle>
          <DialogDescription>Assign each leftover column to a field, or ignore it. Saving clears this lead from the review queue.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {entries.length === 0 ? <div className="text-sm text-gray-400">No unmapped columns.</div> :
            entries.map(([k, v]) => (
              <div key={k} className="grid grid-cols-2 gap-2 items-center border border-gray-200 rounded p-2" data-testid={`map-row-${k}`}>
                <div className="text-sm min-w-0"><div className="font-medium text-gray-900 truncate">{k}</div><div className="text-xs text-gray-500 truncate">{v}</div></div>
                <Select value={mapping[k] || "ignore"} onValueChange={(val) => setMapping({ ...mapping, [k]: val })}>
                  <SelectTrigger data-testid={`map-select-${k}`}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="ignore">Ignore</SelectItem>
                    {MAP_TARGETS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="map-save-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save & resolve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
